import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  CloudDownloadOutlined,
  KeyboardOutlined,
  LaptopWindowsOutlined,
  OfflineBoltOutlined,
  SyncOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { fetchLatestRelease, pickExeInstaller, submitFeatureRequest, WINDOWS_ARCHITECTURES } from '../services/windowsClientService';
import { Card, CardContent, CardHeader, Button } from '../design-system/components';
import spacing from '../design-system/spacing';
import usePageTitle from '../hooks/usePageTitle';

// ── helpers ──────────────────────────────────────────────────────────────────

const formatPublishedDate = (isoString, locale) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const validateFeatureForm = (values, t) => {
  const errors = {};
  if (!values.title.trim()) {
    errors.title = t('featureRequest.titleRequired');
  } else if (values.title.trim().length < 5) {
    errors.title = t('featureRequest.titleTooShort');
  } else if (values.title.trim().length > 200) {
    errors.title = t('featureRequest.titleTooLong');
  }
  if (!values.description.trim()) {
    errors.description = t('featureRequest.descriptionRequired');
  } else if (values.description.trim().length < 10) {
    errors.description = t('featureRequest.descriptionTooShort');
  } else if (values.description.trim().length > 2000) {
    errors.description = t('featureRequest.descriptionTooLong');
  }
  return errors;
};

// ── sub-components ────────────────────────────────────────────────────────────

const BenefitCard = ({ icon: Icon, title, desc, delay }) => (
  <Grid item xs={12} sm={6} md={3}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: delay ?? 0 }}
    >
      <Card
        variant="outlined"
        sx={{ height: '100%' }}
      >
        <CardContent>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: `${spacing.borderRadius.md}px`,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Icon sx={{ color: 'primary.contrastText', fontSize: 24 }} aria-hidden="true" />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color={'text.secondary'} sx={{ lineHeight: 1.6 }}>
            {desc}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  </Grid>
);

const GuideStep = ({ number, title, desc }) => (
  <Grid item xs={12} md={4}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 18,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color={'text.secondary'} sx={{ lineHeight: 1.65 }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

// ── DownloadSection ───────────────────────────────────────────────────────────

const DownloadSection = () => {
  const { t, i18n } = useTranslation('windowsClient');
  const locale = (i18n.resolvedLanguage || i18n.language || 'es').substring(0, 2);

  const { data: release, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['windows-release'],
    queryFn: fetchLatestRelease,
    staleTime: 5 * 60 * 1000,
  });

  const publishedDate = release ? formatPublishedDate(release.publishedAt, locale) : null;
  const installers = WINDOWS_ARCHITECTURES.map((arch) => ({
    arch,
    installer: !isLoading && !isError ? pickExeInstaller(release, arch) : null,
  }));
  const hasInstaller = installers.some(({ installer }) => installer);

  return (
    <Card>
      <CardHeader title={t('download.title')} />
      <CardContent>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} aria-label={t('download.loading')} />
            <Typography variant="body2" color={'text.secondary'}>
              {t('download.loading')}
            </Typography>
          </Box>
        )}

        {isError && !isLoading && (
          <Alert
            severity="warning"
            action={
              <Button variant="text" size="small" color="inherit" onClick={() => refetch()}>
                {t('errors.retry')}
              </Button>
            }
          >
            {error?.message?.includes('not configured')
              ? t('errors.notConfigured')
              : error?.message?.startsWith('Invalid Qt')
                ? t('errors.invalidManifest')
                : t('errors.fetchFailed')}
          </Alert>
        )}

        {release && !isLoading && !isError && (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={700} color={'primary.dark'}>
                {t('download.version', { version: release.version })}
              </Typography>
              {publishedDate && (
                <Typography variant="body2" color={'text.secondary'}>
                  {t('download.publishedAt', { date: publishedDate })}
                </Typography>
              )}
            </Box>

            {!hasInstaller && (
              <Alert severity="info">{t('download.noInstaller')}</Alert>
            )}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          {t('download.architectureHelp')}
        </Typography>
        {hasInstaller && (
          <Alert severity="warning" role="note" id="windows-installer-signature-warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('download.signatureWarningTitle')}
            </Typography>
            {t('download.signatureWarning')}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
          {installers.map(({ arch, installer }) => (
            <Box key={arch} sx={{ minWidth: 0 }}>
              <Button
                variant="contained"
                component={installer ? 'a' : 'button'}
                href={installer?.url}
                download={installer ? true : undefined}
                disabled={!installer}
                aria-describedby={`download-${arch}-metadata${hasInstaller ? ' windows-installer-signature-warning' : ''}`}
                startIcon={<CloudDownloadOutlined />}
                sx={{ minHeight: 44, minWidth: 44 }}
              >
                {t(`download.architectures.${arch}`)}
              </Button>
              <Box id={`download-${arch}-metadata`} sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                {installer ? (
                  <>
                    <Typography variant="body2" color="text.secondary">{installer.file}</Typography>
                    {installer.fileSize && (
                      <Typography variant="body2" color="text.secondary">
                        {t('download.fileSize', { size: (installer.fileSize / 1024 / 1024).toLocaleString(locale, { maximumFractionDigits: 1 }) })}
                      </Typography>
                    )}
                    {installer.sha256 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('download.sha256', { checksum: installer.sha256 })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {isLoading ? t('download.loading') : t('download.archUnavailable', { architecture: t(`download.architectures.${arch}`) })}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
        {hasInstaller && release.checksums && (
          <Link
            href={release.checksums}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('download.checksumsAriaLabel')}
            underline="hover"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'inline-block', mt: 2 }}
          >
            {t('download.checksums')}
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

// ── ChangelogSection ──────────────────────────────────────────────────────────

const ChangelogSection = () => {
  const { t } = useTranslation('windowsClient');

  const { data: release, isLoading, isError } = useQuery({
    queryKey: ['windows-release'],
    queryFn: fetchLatestRelease,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || isError || !release) return null;

  return (
    <Card variant="flat">
      <CardHeader title={t('changelog.title')} />
      <CardContent>
        {release.changelog ? (
          <Typography
            variant="body2"
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              lineHeight: 1.7,
              color: 'text.primary',
              m: 0,
            }}
          >
            {release.changelog}
          </Typography>
        ) : (
          <Typography variant="body2" color={'text.secondary'}>
            {t('changelog.empty')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ── FeatureRequestSection ─────────────────────────────────────────────────────

const FeatureRequestSection = () => {
  const { t } = useTranslation('windowsClient');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [form, setForm] = useState({ title: '', description: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: submitFeatureRequest,
    onSuccess: () => {
      setSubmitted(true);
      setForm({ title: '', description: '' });
      setFormErrors({});
    },
  });

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateFeatureForm(form, t);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    mutation.mutate({ title: form.title.trim(), description: form.description.trim() });
  };

  return (
    <Card>
      <CardHeader title={t('featureRequest.title')} />
      <CardContent>
        <Typography variant="body2" color={'text.secondary'} sx={{ mb: 3 }}>
          {t('featureRequest.description')}
        </Typography>

        {!isAuthenticated ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              gap: 2,
              p: 3,
              borderRadius: `${spacing.borderRadius.md}px`,
              backgroundColor: 'action.selected',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {t('featureRequest.loginCta')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ minHeight: 44, minWidth: 44, flexShrink: 0 }}
            >
              {t('featureRequest.loginButton')}
            </Button>
          </Box>
        ) : submitted ? (
          <Alert
            severity="success"
            icon={<CheckCircleOutline />}
            role="status"
            aria-live="polite"
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {t('featureRequest.successTitle')}
            </Typography>
            <Typography variant="body2">{t('featureRequest.successDesc')}</Typography>
          </Alert>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            aria-label={t('featureRequest.title')}
          >
            {mutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }} role="alert" aria-live="assertive">
                {t('featureRequest.errorGeneric')}
              </Alert>
            )}

            <TextField
              id="fr-title"
              label={t('featureRequest.titleLabel')}
              placeholder={t('featureRequest.titlePlaceholder')}
              value={form.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              error={Boolean(formErrors.title)}
              helperText={formErrors.title || ' '}
              required
              fullWidth
              inputProps={{ maxLength: 200, 'aria-required': 'true' }}
              sx={{ mb: 2 }}
            />

            <TextField
              id="fr-description"
              label={t('featureRequest.descriptionLabel')}
              placeholder={t('featureRequest.descriptionPlaceholder')}
              value={form.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              error={Boolean(formErrors.description)}
              helperText={formErrors.description || ' '}
              required
              fullWidth
              multiline
              minRows={4}
              inputProps={{ maxLength: 2000, 'aria-required': 'true' }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending}
              sx={{ minHeight: 44, minWidth: 44 }}
            >
              {mutation.isPending ? t('featureRequest.submitting') : t('featureRequest.submitButton')}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const WindowsClientPage = () => {
  const { t } = useTranslation('windowsClient');
  usePageTitle(t('page.metaTitle'));

  const benefits = [
    { icon: OfflineBoltOutlined, key: 'offline' },
    { icon: LaptopWindowsOutlined, key: 'performance' },
    { icon: SyncOutlined, key: 'integration' },
    { icon: KeyboardOutlined, key: 'shortcuts' },
  ];

  const requirementItems = [
    t('requirements.os'),
    t('requirements.ram'),
    t('requirements.storage'),
    t('requirements.net'),
  ];

  const guideSteps = [
    { key: 'step1', number: 1 },
    { key: 'step2', number: 2 },
    { key: 'step3', number: 3 },
  ];

  return (
    <Box
      className="wp-page"
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Hero */}
      <Box
        className="wp-heading"
        component="header"
        sx={{
          mb: 5,
          pb: 4,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Typography
            variant="h3"
            component="h1"
            fontWeight={800}
            color={'text.primary'}
            gutterBottom
          >
            {t('page.title')}
          </Typography>
          <Typography
            variant="h5"
            color={'primary.main'}
            fontWeight={600}
            gutterBottom
          >
            {t('page.subtitle')}
          </Typography>
          <Typography
            variant="body1"
            color={'text.secondary'}
            sx={{ maxWidth: 680, lineHeight: 1.7 }}
          >
            {t('page.description')}
          </Typography>
        </motion.div>
      </Box>

      {/* Download */}
      <Box sx={{ mb: 5 }}>
        <DownloadSection />
      </Box>

      {/* Benefits */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          {t('benefits.title')}
        </Typography>
        <Grid container spacing={3}>
          {benefits.map(({ icon, key }, i) => (
            <BenefitCard
              key={key}
              icon={icon}
              title={t(`benefits.${key}.title`)}
              desc={t(`benefits.${key}.desc`)}
              delay={i * 0.07}
            />
          ))}
        </Grid>
      </Box>

      {/* System requirements */}
      <Box sx={{ mb: 5 }}>
        <Card variant="outlined">
          <CardHeader title={t('requirements.title')} />
          <CardContent>
            <Box
              component="ul"
              sx={{ m: 0, pl: 3 }}
              aria-label={t('requirements.title')}
            >
              {requirementItems.map((item) => (
                <Typography
                  key={item}
                  component="li"
                  variant="body2"
                  sx={{ mb: 0.75, lineHeight: 1.6 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Changelog */}
      <Box sx={{ mb: 5 }}>
        <ChangelogSection />
      </Box>

      {/* Quick start guide */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          {t('guide.title')}
        </Typography>
        <Grid container spacing={4}>
          {guideSteps.map(({ key, number }) => (
            <GuideStep
              key={key}
              number={number}
              title={t(`guide.${key}.title`)}
              desc={t(`guide.${key}.desc`)}
            />
          ))}
        </Grid>
      </Box>

      <Divider sx={{ mb: 5 }} />

      {/* Feature request */}
      <Box sx={{ mb: 4 }}>
        <FeatureRequestSection />
      </Box>
    </Box>
  );
};

export default WindowsClientPage;
